import { useState, useRef, useEffect } from 'react'
import Formsy from 'formsy-react'

import Croppie from 'croppie'
import 'croppie/croppie.css'

import { Button, Typography, Avatar, DialogActions, DialogTitle, DialogContent, InputAdornment, Tooltip, IconButton, } from '@mui/material'
import { Upload, VpnKey, Visibility, VisibilityOff } from '@mui/icons-material'

import { C, CenterContent, D, TextFieldFormsy } from '../include'


// ==========================================
// COMPONENTE PARA TROCA DA FOTO DO PERFIL
// ==========================================
const CropPic = ({ api, updateAppData, dialogClose, alert, file }) => {
  const croppieContainerRef = useRef(null); // Ref substitui o getElementById('croppie')
  const croppieInstance = useRef(null);     // Ref para guardar a instância da biblioteca Croppie

  useEffect(() => {
    // Só inicializa se o container existir e o arquivo tiver sido passado
    if (!croppieContainerRef.current || !file) return;

    croppieInstance.current = new Croppie(croppieContainerRef.current, {
      enableExif: false,
      viewport: {
        width: 320,
        height: 320,
        type: 'circle',
      },
      boundary: {
        width: 340,
        height: 340,
      },
      url: URL.createObjectURL(file), // Usa o arquivo recebido via prop
    });

    // Função de limpeza: destrói a instância do Croppie ao fechar o modal para evitar memory leak
    return () => {
      if (croppieInstance.current) {
        croppieInstance.current.destroy();
      }
    };
  }, [file]);

  const cropButton = () => {
    if (!croppieInstance.current) return;

    croppieInstance.current
      .result({
        size: 'viewport',
        format: 'png',
        type: 'blob',
      })
      .then((r) => {
        const croppedPic = new FormData();
        croppedPic.append('file', r, 'croppie.png');
        
        api.post('profile', croppedPic).then((res) => {
          dialogClose();
          if (res.code === 0) {
            updateAppData();
          } else {
            alert(res.msg);
          }
        });
      });
  };

  return (
    <D style={{ padding: '3rem' }}>
      {/* Container referenciado para o Croppie injetar o canvas */}
      <D ref={croppieContainerRef} /> 
      <D h="30px" />
      <DialogActions>
        <Button 
          variant="outlined" 
          color="primary" 
          onClick={(e) => {
            e.currentTarget.blur();
            dialogClose();
          }}
        >
          Cancelar
        </Button>

        <Button 
          variant="outlined" 
          color="primary" 
          onClick={cropButton}
        >
          <Upload /> Enviar
        </Button>
      </DialogActions>
    </D>
  );
};


// ==========================================
// COMPONENTE: ChangePwd
// ==========================================
const ChangePwd = ({ user, api, alert, dialogClose }) => {
  const [password, setPassword] = useState('');
  const [passwordC, setPasswordC] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordC, setShowPasswordC] = useState(false);

  const handleSave = () => {
    // Lemos os dados estáticos direto da prop user (não precisam ficar no state duplicados)
    const dt = {
      displayName: user?.name || '',
      email: user?.email || '',
      password: password
    };
    
    api.put('/user_data/', dt)
      .then(r => {
        dialogClose();
        alert(r.msg);
      })
      .catch(r => alert(r.msg));
  };

  return (
    <Formsy onValidSubmit={handleSave}>
      <DialogTitle style={{ minWidth: '400px' }}>Trocar senha</DialogTitle>
      <DialogContent>
        <D h="0.5rem" />

        <input
          type="email"
          name="username"
          autoComplete="username"
          value={user?.email || ''}
          readOnly
          hidden
        />

        <TextFieldFormsy
          required
          key={showPassword ? "text" : "password"}
          type={showPassword ? "text" : "password"}
          name="password"
          label="Nova senha"
          autoComplete="new-password"
          value={password}
          onChange={v => setPassword(v)}
          validations={{ minLength: 8 }}
          validationErrors={{ minLength: 'Ao menos 8 caracteres' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ gap: 0.5 }}>
                <Tooltip title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => setShowPassword(s => !s)}
                    onMouseDown={e => e.preventDefault()}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </Tooltip>
                <VpnKey color="action" fontSize="small" sx={{ mr: 0.5 }} />
              </InputAdornment>
            )
          }}
        />

        <D size="1rem" />

        <TextFieldFormsy
          required
          key={showPasswordC ? 'textc' : 'passwordc'}
          type={showPasswordC ? 'text' : 'password'}
          name="passwordConfirm"
          label="Confirmar senha"
          autoComplete="new-password"
          value={passwordC}
          onChange={v => setPasswordC(v)}
          validations="equalsField:password"
          validationErrors={{ equalsField: 'Senhas diferentes' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ gap: 0.5 }}>
                <Tooltip title={showPasswordC ? 'Ocultar senha' : 'Mostrar senha'}>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => setShowPasswordC(s => !s)}
                    onMouseDown={e => e.preventDefault()}
                  >
                    {showPasswordC ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </Tooltip>
                <VpnKey color="action" fontSize="small" sx={{ mr: 0.5 }} />
              </InputAdornment>
            ),
          }}
        />
        <D h="0.5rem" />
      </DialogContent>

      <DialogActions>
        <Button 
          variant="outlined" 
          color="primary" 
          onClick={(e) => {
            e.currentTarget.blur();
            dialogClose();
          }}
        >
          Cancelar
        </Button>

        <Button 
          variant="outlined" 
          color="primary" 
          type="submit" // Agora o form lida com a submissão via onValidSubmit do Formsy
        >
          Salvar
        </Button>
      </DialogActions>
    </Formsy>
  );
};


// ==========================================
// COMPONENTE PRINCIPAL: MyProfile
// ==========================================
export const MyProfile = ({ user, api, alert, dialog, dialogClose, updateAppData, edit }) => {
  const fileInputRef = useRef(null); // Substitui o getElementById('avatarPic')

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // valida se é imagem
    if (!file.type || !file.type.startsWith('image/')) {
      alert('O arquivo selecionado não é uma imagem válida !');
      e.target.value = null; // Limpa o input se der erro
      return;
    }

    // abre CropPic passando o objeto `file` nativo
    dialog(
      <CropPic
        api={api}
        updateAppData={updateAppData}
        file={file} 
        dialogClose={dialogClose}
        alert={alert}
      />
    );
    
    // Limpa o input para garantir que o onChange dispare se o usuário escolher a mesma foto de novo
    e.target.value = null;
  };

  return (
    <CenterContent className="r2c e aF scB" style={{ padding: '2rem 3rem' }} p="11">
      <D className="col aC">
        {user?.photo ? (
          <img 
            alt="Foto do perfil" 
            src={C.baseURL + 'pic/' + user.photo} 
            style={{ width: '200px', height: '200px', borderRadius: '50%', objectFit: 'cover' }}
          />
        ) : (
          <Avatar sx={{ width: 200, height: 200 }} />
        )}
        
        <input
          ref={fileInputRef} // Ligação da referência no DOM
          name="file"
          className="hide"
          type="file"
          accept="image/*"
          capture="camera"
          style={{ display: 'none' }} // Garantia adicional de ocultação
          onChange={handleFileChange}
        />

        <D h="2rem" />
        <D>
          <Typography variant="h4" color="inherit">{user?.name}</Typography>
        </D>

        <D h="2rem" />
        <Button 
          variant="outlined" 
          color="primary" 
          onClick={() => fileInputRef.current.click()} // Disparo via Ref
        >
          <Upload /> Trocar foto
        </Button>
        <D e />
      </D>
      
      <D size="3rem" h="3rem" />
      
      <D className="col e aF">
        <Formsy>
          <TextFieldFormsy type="text" name="name" label="Nome" value={user?.name || ''} disabled={!edit} />
          <TextFieldFormsy type="text" name="email" label="E-mail" value={user?.email || ''} disabled={!edit} />
          <TextFieldFormsy type="text" name="perfil" label="Perfil de Acesso" value={user?.role || ''} disabled={!edit} />
          <TextFieldFormsy type="text" name="court" label={C.businessLabel} value={user?.court || ''} disabled={!edit} />
          <TextFieldFormsy type="text" name="section" label={C.divisionLabel} value={user?.section || ''} disabled={!edit} />
          <D h="2rem" />
          <D className="row w aC">
            <Button
              variant="outlined"
              color="primary"
              onClick={() => dialog(
                <ChangePwd
                  api={api}
                  user={user}
                  alert={alert}
                  dialogClose={dialogClose} 
                />
              )}
            >
              <VpnKey /> Trocar senha
            </Button>
          </D>
          <D h="2rem" />
        </Formsy>
      </D>
    </CenterContent>
  );
};
